<template>
  <div
    class="flex flex-wrap md:flex-nowrap justify-between md:justify-normal mb-10 gap-4"
  >
    <div class="w-full md:max-w-[400px]">
      <div class="relative">
        <Search class="text-neutral-400 w-4 h-4 absolute top-3 left-3" />
        <Input
          v-model="searchTerm"
          placeholder="Search all projects..."
          class="pl-8"
        />
      </div>
    </div>
    <div
      class="w-full md:w-auto md:ml-auto flex gap-2.5 justify-between md:justify-normal"
    >
      <Protect :permissions="[SYS_PERMISSIONS.SYS_PROJECTS_CREATE]">
        <CreateProjectDialog />
      </Protect>
      <Protect :permissions="[SYS_PERMISSIONS.SYS_PROJECTS_IMPORT]">
        <ProjectUploader />
      </Protect>
      <Protect :permissions="[SYS_PERMISSIONS.SYS_MEMBERSHIPS_MANAGE]">
        <InvitationMemberDialog>
          <template #trigger>
            <IconButton variant="outline">
              <UserPlus />
            </IconButton>
          </template>
        </InvitationMemberDialog>
      </Protect>
    </div>
  </div>

  <div v-if="isLoadingProjects" class="flex flex-wrap gap-8 items-stretch">
    <ProjectSkeleton v-for="i in 5" :key="`project-skeleton-${i}`" />
  </div>
  <div v-else>
    <div v-if="projects?.length" class="flex flex-wrap gap-4 items-stretch">
      <ProjectItem
        v-for="project in projects"
        :key="project.id"
        :project="project"
        :total-count="projects.length"
      />
      <div class="visibility-hidden h-0 flex-[1_1_200px]" />
      <div class="visibility-hidden h-0 flex-[1_1_200px]" />
      <div class="visibility-hidden h-0 flex-[1_1_200px]" />
      <div class="visibility-hidden h-0 flex-[1_1_200px]" />
      <div class="visibility-hidden h-0 flex-[1_1_200px]" />
      <div class="visibility-hidden h-0 flex-[1_1_200px]" />
    </div>
    <div v-else-if="(!projects?.length && searchTerm !== '') || hasProjects">
      <div
        class="relative h-[250px] rounded-lg flex justify-center items-center"
      >
        <div class="space-y-3 text-center relative">
          <div class="text-xl font-semibold text-white">No projects found</div>
          <div class="text-neutral-400">Let’s try again</div>
        </div>
      </div>
    </div>
    <div v-else>
      <div
        class="relative h-[250px] rounded-lg flex justify-center items-center"
      >
        <div class="space-y-3 text-center relative">
          <div class="text-xl font-semibold text-white">No projects yet</div>
          <div class="text-neutral-400">Create your first project</div>
          <Protect :permissions="[SYS_PERMISSIONS.SYS_PROJECTS_CREATE]">
            <CreateProjectDialog />
          </Protect>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { Search, UserPlus } from 'lucide-vue-next';
import { watchDebounced } from '@vueuse/core';
import { Input } from '@ui/components/input';
import { SYS_PERMISSIONS } from '@/constants.js';
import Protect from '@/hoc/Protect.vue';
import { IconButton } from '@ui/components/icon-button';
import requests from '@vuex/requests.js';

import { useStore } from 'vuex';
import { queries } from '@/queries/index.js';
import ProjectItem from './components/projects/ProjectItem.vue';
import ProjectSkeleton from './components/projects/ProjectSkeleton.vue';
import ProjectUploader from './components/projects/ProjectUploader.vue';
import CreateProjectDialog from './components/projects/CreateProjectDialog.vue';
import InvitationMemberDialog from './components/members/InvitationMemberDialog.vue';

const searchTerm = ref('');

const hasProjects = ref(false);
const store = useStore();

const {
  isLoading: isLoadingProjects,
  data: projects,
  refetch,
} = useQuery({
  queryKey: queries.workspace.projects.queryKey,
  queryFn: () =>
    requests.workspaces.projects
      .list({
        workspaceId: store.getters['workspace/getId'],
        query: searchTerm.value,
      })
      .then(data => {
        hasProjects.value = hasProjects.value || data.length > 0;
        return data;
      }),
});

watchDebounced(searchTerm, refetch, { debounce: 300 });
</script>
